import {
  Box,
  Flex,
  HStack,
  Heading,
  Text,
  Icon,
  Badge,
  Skeleton,
  SimpleGrid,
  Button,
} from '@chakra-ui/react'
import { useMemo } from 'react'
import { useQuery } from '@tanstack/react-query'
import { useNavigate } from 'react-router-dom'
import {
  LuRefreshCw,
  LuArrowRight,
  LuClock,
  LuListChecks,
  LuCircleCheck as LuCheckCircle2,
  LuCircleAlert as LuAlertCircle,
  LuTriangleAlert as LuAlertTriangle,
  LuShare2,
} from 'react-icons/lu'
import axiosClient from '../../api/axios'
import { formatDistanceToNow } from 'date-fns'
import { fr } from 'date-fns/locale'
import { pathologyDisplayName } from '../Lab/BlotAnalysisWorkspace/utils/displayName'
import { computeBlotCaseStatus } from '../Lab/BlotAnalysisWorkspace/blotCaseStatus'
import { useTranslation } from 'react-i18next'

interface BlotCaseRaw {
  id: string
  sampleId: string
  filename: string
  totalLanes: number
  pathologyName?: string
  createdAt: string
  updatedAt: string
  selectedPositiveControlId?: number | null
  selectedPositiveControl2Id?: number | null
  selectedPositiveControl3Id?: number | null
  manualBlotResults?: Record<string, string> | null
  bandCalibration?: any
  hiddenBlotIds?: number[]
  /** Present when the case was shared WITH the current user (vs. owned). */
  sharedBy?: { id: string; contactName: string | null; email: string; companyName: string | null } | null
  shareRole?: 'viewer' | 'editor'
}

// Status now comes from the shared helper — the previous local implementation
// keyed off `totalLanes`, which the blot pipeline never fills (it writes
// `totalBlots`), so no case could ever reach 'validated' and validated series
// stayed forever in "Tâches à traiter".

export default function InProgressCases() {
  const { t } = useTranslation()
  const navigate = useNavigate()

  // CLE PREFIXEE PAR 'blotRecentCases' A DESSEIN.
  //
  // Le workspace invalide ['blotRecentCases'] a chaque validation, suppression
  // ou nouvelle analyse (quatre endroits). Sous l'ancienne cle
  // 'homeRecentBlotCases', aucune de ces invalidations ne portait jusqu'ici :
  // on validait un cas, on revenait a l'accueil, et il restait dans les taches
  // a traiter. React Query invalide par prefixe, donc cette forme rattache le
  // panneau aux invalidations existantes sans en ajouter une cinquieme a
  // oublier. Le second segment reste 'home' pour ne pas entrer en collision
  // avec les cles de BlotRecentCases, qui sont ['blotRecentCases', <limit>, …].
  const { data, isLoading } = useQuery<{ analyses: BlotCaseRaw[] }>({
    queryKey: ['blotRecentCases', 'home'],
    queryFn: async () => {
      // scope=personal → own cases + shared-with-me only. Without it, an admin
      // gets the whole fleet's cases here (the endpoint's admin default is all
      // users), so "Tâches à traiter" would count other pilots' work.
      const response = await axiosClient.get('/blot-analysis/recent-cases?limit=50&scope=personal')
      return response.data
    },
    staleTime: 30_000,
    // Une validation faite ailleurs doit se voir en revenant ici — c'est le
    // meme raisonnement que « Mon activite recente », qui se recharge deja au
    // retour sur l'onglet. Le staleTime de 30 s suffit a brider les appels.
    refetchOnWindowFocus: true,
  })

  const buckets = useMemo(() => {
    const list = data?.analyses ?? []
    const inProgress: BlotCaseRaw[] = []
    const pending: BlotCaseRaw[] = []
    const sharedWithMe: BlotCaseRaw[] = []
    for (const c of list) {
      // A case shared with the recipient always surfaces here regardless of
      // its computed status — otherwise a case that arrives already
      // "validated" (positive control set, calibration in place) wouldn't
      // appear at all, leaving the recipient with no way to discover it.
      if (c.sharedBy) {
        sharedWithMe.push(c)
        continue
      }
      const s = computeBlotCaseStatus(c)
      if (s === 'in-progress') inProgress.push(c)
      else if (s === 'pending') pending.push(c)
    }
    inProgress.sort((a, b) => new Date(b.updatedAt).getTime() - new Date(a.updatedAt).getTime())
    pending.sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime())
    sharedWithMe.sort((a, b) => new Date(b.updatedAt).getTime() - new Date(a.updatedAt).getTime())
    return { inProgress, pending, sharedWithMe }
  }, [data])

  const total = buckets.inProgress.length + buckets.pending.length
  const sharedCount = (data?.analyses ?? []).filter((c) => c.sharedBy).length

  if (isLoading) {
    return (
      <Box bg="white" borderRadius="2xl" boxShadow="sm" border="1px solid" borderColor="gray.100" p={6}>
        <Skeleton height="28px" width="180px" mb={4} />
        <SimpleGrid columns={{ base: 1, sm: 2, lg: 3, xl: 4 }} spacing={4}>
          {[0, 1, 2, 3].map((i) => (
            <Skeleton key={i} height="120px" borderRadius="xl" />
          ))}
        </SimpleGrid>
      </Box>
    )
  }

  return (
    <Box
      data-tour-id="home-cases"
      bg="white"
      borderRadius="2xl"
      boxShadow="sm"
      border="1px solid"
      borderColor="gray.100"
      p={6}
      position="relative"
      overflow="hidden"
    >
      {/* Top accent strip */}
      <Box
        position="absolute"
        top={0}
        left={0}
        right={0}
        h="3px"
        bgGradient="linear(to-r, orange.400, amber.400, yellow.400)"
      />

      {(() => {
        // Status colour scheme — drives the colored status pill on the left.
        //   0 cases     → green ("Tout est validé — bravo")
        //   1-5 cases   → orange (manageable backlog)
        //   ≥ 6 cases   → red (significant backlog, prioritise)
        const tone = total === 0 ? 'green' : total <= 5 ? 'orange' : 'red'
        const subtitle = total === 0
          ? t('home.inProgress.allDone')
          : total === 1
            ? t('home.inProgress.onePending')
            : `${total} cas en attente d’action`
        // Use raw hex values — Chakra's default theme doesn't ship `emerald`,
        // `amber`, `rose`, so referencing them silently produced no gradient.
        const pillGradient = tone === 'green'
          ? 'linear(135deg, #16a34a, #059669)'   // green-600 → emerald-600
          : tone === 'orange'
            ? 'linear(135deg, #f97316, #d97706)' // orange-500 → amber-600
            : 'linear(135deg, #ef4444, #e11d48)' // red-500 → rose-600
        const pillShadow = tone === 'green'
          ? '0 6px 18px rgba(34,197,94,0.35)'
          : tone === 'orange'
            ? '0 6px 18px rgba(251,146,60,0.35)'
            : '0 6px 18px rgba(239,68,68,0.40)'
        const PillIcon = tone === 'green'
          ? LuCheckCircle2
          : tone === 'orange'
            ? LuAlertCircle
            : LuAlertTriangle
        return (
          <Flex justify="space-between" align="center" mb={5} flexWrap="wrap" gap={3}>
            <HStack spacing={3} align="center">
              {/* Status pill — its colour reflects the workload: green / orange / red. */}
              <Box
                bgGradient={pillGradient}
                color="white"
                borderRadius="xl"
                px={3.5}
                py={2}
                display="flex"
                alignItems="center"
                gap={2}
                boxShadow={pillShadow}
                minH="44px"
              >
                <Icon as={PillIcon} boxSize={5} />
                <Text fontSize="lg" fontWeight="bold" lineHeight="1">
                  {total}
                </Text>
              </Box>
              <Box>
                <Heading size="md" color="gray.800">{t('home.inProgress.title')}</Heading>
                <Text fontSize="xs" color="gray.500" mt={0.5}>
                  {subtitle}
                </Text>
              </Box>
            </HStack>
            {sharedCount > 0 && (
              <HStack
                spacing={2}
                bg="teal.50"
                color="teal.700"
                px={3}
                py={1.5}
                borderRadius="full"
                border="1px solid"
                borderColor="teal.200"
                cursor="pointer"
                onClick={() => navigate('/lab/registry')}
                _hover={{ bg: 'teal.100' }}
              >
                <Icon as={LuShare2} boxSize={3.5} />
                <Text fontSize="xs" fontWeight="700">
                  {sharedCount === 1
                    ? '1 cas partagé avec vous'
                    : `${sharedCount} cas partagés avec vous`}
                </Text>
              </HStack>
            )}
          </Flex>
        )
      })()}

      {/* ── Shared with me (always visible, regardless of computed status) ──
          A case shared via the Invite button can arrive already validated
          (positive control set, calibration done), so it would otherwise
          be filtered out of inProgress + pending and the recipient would
          have no way to discover it. We surface them in a dedicated teal
          ribbon at the top of the card. */}
      {buckets.sharedWithMe.length > 0 && (
        <Box
          mb={5}
          p={4}
          borderRadius="xl"
          bgGradient="linear(135deg, teal.50, cyan.50)"
          border="1px solid"
          borderColor="teal.200"
        >
          <HStack spacing={3} mb={3} align="center">
            <Box
              w={9} h={9}
              borderRadius="lg"
              bgGradient="linear(135deg, teal.500, cyan.500)"
              color="white"
              display="grid"
              sx={{ placeItems: 'center' }}
              boxShadow="0 6px 16px rgba(20, 184, 166, 0.35)"
            >
              <Icon as={LuShare2} boxSize={4} />
            </Box>
            <Box flex={1}>
              <Text fontSize="sm" fontWeight="700" color="teal.800">
                {buckets.sharedWithMe.length === 1
                  ? t('home.inProgress.oneShared')
                  : t('home.inProgress.manyShared', { count: buckets.sharedWithMe.length })}
              </Text>
              <Text fontSize="xs" color="teal.700">
                Cliquez pour ouvrir et consulter.
              </Text>
            </Box>
          </HStack>
          <SimpleGrid columns={{ base: 1, sm: 2, lg: 3 }} spacing={3}>
            {buckets.sharedWithMe.slice(0, 6).map((c) => (
              <Box
                key={c.id}
                bg="white"
                borderRadius="lg"
                border="1px solid"
                borderColor="teal.100"
                p={3}
                cursor="pointer"
                transition="all 0.2s"
                onClick={() => navigate(`/lab/blot-analysis/${c.id}`)}
                _hover={{ borderColor: 'teal.300', transform: 'translateY(-2px)', boxShadow: '0 6px 16px -8px rgba(20,184,166,0.35)' }}
                role="group"
              >
                <HStack spacing={2} mb={1.5}>
                  <Icon as={LuShare2} color="teal.600" boxSize={3} />
                  <Text fontSize="2xs" fontWeight="600" color="teal.700" noOfLines={1}>
                    Partagé par {c.sharedBy!.contactName || c.sharedBy!.email.split('@')[0]}
                  </Text>
                </HStack>
                <Text fontWeight="700" fontSize="sm" color="gray.800" noOfLines={1}>
                  {c.filename}
                </Text>
                <Text fontSize="xs" color="gray.500" noOfLines={1}>
                  {pathologyDisplayName(c.pathologyName) || t('home.inProgress.notSet')}
                </Text>
                <HStack justify="space-between" mt={2}>
                  <Text fontSize="2xs" color="gray.400">
                    {formatDistanceToNow(new Date(c.updatedAt), { addSuffix: true, locale: fr })}
                  </Text>
                  <HStack spacing={1} fontSize="2xs" fontWeight="600" color="teal.600" _groupHover={{ gap: 2 }}>
                    <Text>Ouvrir</Text>
                    <Icon as={LuArrowRight} boxSize={3} />
                  </HStack>
                </HStack>
              </Box>
            ))}
          </SimpleGrid>
          {buckets.sharedWithMe.length > 6 && (
            <Flex justify="center" mt={2}>
              <Button variant="ghost" size="xs" colorScheme="teal" rightIcon={<LuArrowRight />} onClick={() => navigate('/lab/registry')}>
                + {buckets.sharedWithMe.length - 6} autres
              </Button>
            </Flex>
          )}
        </Box>
      )}

      {total === 0 ? (
        <Box
          bg="green.50"
          border="1px solid"
          borderColor="green.100"
          borderRadius="xl"
          p={6}
          textAlign="center"
        >
          <Icon as={LuListChecks} color="green.500" boxSize={8} mb={2} />
          <Text fontSize="sm" color="green.700" fontWeight="semibold">
            Aucun cas en attente
          </Text>
          <Text fontSize="xs" color="green.600" mt={1}>
            Tu peux démarrer une nouvelle analyse depuis le module Blot Interpreter.
          </Text>
        </Box>
      ) : (
        /* CHIPS compacts sur deux colonnes : chaque cas tient sur une petite
           ligne à deux niveaux (fichier / recherche) d'une demi-largeur —
           10 cas en 5 rangées, moins haut que l'ancienne grille de cartes,
           sans qu'une ligne ne s'étire sur toute la page. */
        <SimpleGrid columns={{ base: 1, md: 2 }} spacing={2}>
          {/* 6 chips : 3 par colonne — le panneau reste court, le registre
              porte le reste via « + N autres cas ». */}
          {[...buckets.inProgress, ...buckets.pending].slice(0, 6).map((c) => {
            const status = computeBlotCaseStatus(c)
            const isInProgress = status === 'in-progress'
            const accent = isInProgress ? 'orange' : 'gray'
            return (
              <Flex
                key={c.id}
                align="center"
                gap={2.5}
                px={3}
                py={2}
                borderRadius="lg"
                border="1px solid"
                borderColor="gray.100"
                cursor="pointer"
                position="relative"
                overflow="hidden"
                transition="all 0.15s"
                onClick={() => navigate(`/lab/blot-analysis/${c.id}`)}
                _hover={{
                  bg: isInProgress ? 'orange.50' : 'gray.50',
                  borderColor: isInProgress ? 'orange.200' : 'gray.200',
                }}
                role="group"
                sx={{
                  // Liseré d'accent permanent à gauche : le statut se lit
                  // d'un coup d'œil, sans badge qui prendrait de la place.
                  '&::before': {
                    content: '""',
                    position: 'absolute',
                    left: 0,
                    top: 0,
                    bottom: 0,
                    width: '3px',
                    background: isInProgress
                      ? 'linear-gradient(to bottom, #fb923c, #f59e0b)'
                      : 'linear-gradient(to bottom, #cbd5e0, #a0aec0)',
                  },
                }}
              >
                <Box
                  w={7}
                  h={7}
                  borderRadius="md"
                  bg={`${accent}.50`}
                  color={`${accent}.500`}
                  display="flex"
                  alignItems="center"
                  justifyContent="center"
                  flexShrink={0}
                >
                  <Icon as={isInProgress ? LuRefreshCw : LuClock} boxSize={3.5} />
                </Box>
                <Box flex={1} minW={0}>
                  <HStack spacing={1.5}>
                    <Text fontWeight="600" fontSize="xs" color="gray.800" noOfLines={1}>
                      {c.filename}
                    </Text>
                    {c.sharedBy && <Icon as={LuShare2} boxSize={2.5} color="teal.600" flexShrink={0} />}
                  </HStack>
                  <Text fontSize="2xs" color="gray.500" noOfLines={1}>
                    {pathologyDisplayName(c.pathologyName) || t('home.inProgress.notSet')}
                  </Text>
                </Box>
                <Box textAlign="right" flexShrink={0}>
                  <Text fontSize="2xs" color="gray.400" whiteSpace="nowrap">
                    {formatDistanceToNow(new Date(c.updatedAt), { addSuffix: true, locale: fr })
                      .replace('environ ', '~')}
                  </Text>
                  <HStack
                    spacing={1}
                    fontSize="2xs"
                    fontWeight="semibold"
                    color={`${accent}.500`}
                    justify="flex-end"
                    opacity={{ base: 1, md: 0 }}
                    transition="opacity 0.15s"
                    _groupHover={{ opacity: 1 }}
                  >
                    <Text>Reprendre</Text>
                    <Icon as={LuArrowRight} boxSize={3} />
                  </HStack>
                </Box>
              </Flex>
            )
          })}
        </SimpleGrid>
      )}

      {total > 6 && (
        <Flex justify="center" mt={3}>
          <Button
            variant="ghost"
            size="sm"
            rightIcon={<LuArrowRight />}
            // Vers le registre, avec l'intention : liste dépliée et filtre
            // « À traiter » déjà posé — on atterrit sur le reste à faire,
            // pas sur une liste repliée à re-filtrer.
            onClick={() => navigate('/lab/registry', { state: { openList: true, statusFilter: 'todo' } })}
          >
            + {total - 6} autres cas
          </Button>
        </Flex>
      )}
    </Box>
  )
}
